import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import ActionIcon from '@components/UI/ActionIcon'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { useIntl } from '@context/Internationalization/useIntl'
import useMultiFrameControls from '@context/MultiframeControls/useMultiframControl'
import { EventTrackingNames } from '@enums'
import { selectIsUnlockedCampaign } from '@store/campaigns'
import { IApplicationState } from '@store/index'
import { selectMediaObject } from '@store/mediaObjects'
import { selectCampaignId } from '@store/router'
import { selectActiveSubmissionMediaObject, selectIsTextSubmission } from '@store/submissions'
import Theme from '@theme'
import { Link } from '@tribegroup'

type Props = {
  submissionId: number
}

export const ActionBarIconDownload: React.FC<Props> = ({ submissionId }) => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match) as number
  const { activeFrameNumber } = useMultiFrameControls()
  const { intl } = useIntl()

  const { mediaObjectId, isUnlockedCampaign, mediaObject } = useSelector(
    (state: IApplicationState) => {
      const isUnlockedCampaign = selectIsUnlockedCampaign(state, campaignId)
      if (selectIsTextSubmission(state, submissionId)) {
        return {
          isUnlockedCampaign,
          mediaObject: null,
          mediaObjectId: null,
        }
      }

      const activeMediaObject = selectActiveSubmissionMediaObject(
        state,
        submissionId,
        activeFrameNumber ?? 0,
      )
      const mediaObjectId = activeMediaObject?.id
      const mediaObject = selectMediaObject(state, mediaObjectId)

      return {
        mediaObjectId,
        isUnlockedCampaign,
        mediaObject,
      }
    },
    shallowEqual,
  )

  const downloadUrl = mediaObject?.download_media_url

  if (!mediaObjectId || !mediaObject || !isUnlockedCampaign || !downloadUrl) {
    return null
  }

  return (
    <ConnectedTrackedRoute
      eventName={EventTrackingNames.DownloadLicensedContent}
      campaignId={campaignId}
      submissionId={submissionId}
      mediaObjectId={mediaObjectId}
    >
      <Link href={downloadUrl} target="_blank" download>
        <ActionIcon
          glyph="license-content"
          color={Theme.grey900Color}
          size={1.5}
          tooltip={intl.formatMessage({ id: 'submission.card.action.download' })}
          elementName="download"
        />
      </Link>
    </ConnectedTrackedRoute>
  )
}
